import { BorrowerOnboardingDetails, OnboardingDetailsRole } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { CustomRequiredFile } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/types';
import {
  DocumentGroupKey,
  RequiredFilesByDocumentTypeGroupMap,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/components/UploadDocuments/maps/requiredFilesByDocumentTypeGroup.map';

export type RequiredFilesDocumentGroupView = {
  groupKey: DocumentGroupKey;
  titleSuffix?: string;
  files: CustomRequiredFile[];
};

export type RequiredFilesDocumentGroupSortMode = 'pending' | 'uploaded';

interface UseRequiredFilesDocumentGroupsParams {
  files: CustomRequiredFile[];
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  sortMode: RequiredFilesDocumentGroupSortMode;
}

const sortGroupFilesByMode = (
  files: CustomRequiredFile[],
  sortMode: RequiredFilesDocumentGroupSortMode,
): CustomRequiredFile[] => {
  if (sortMode === 'pending') {
    return RequiredFilesByDocumentTypeGroupMap.sortPendingGroupFiles(files);
  }

  return RequiredFilesByDocumentTypeGroupMap.sortUploadedGroupFiles(files);
};

export const useRequiredFilesDocumentGroups = ({
  files,
  paymentMethodDetailEntity,
  sortMode,
}: UseRequiredFilesDocumentGroupsParams): RequiredFilesDocumentGroupView[] => {
  return useMemo(() => {
    const loanOnboardingDetails = paymentMethodDetailEntity.loan?.onboardingDetails ?? [];

    const resolveOnboardingDetailsRole = (requiredFile: CustomRequiredFile) =>
      RequiredFilesByDocumentTypeGroupMap.getOnboardingDetailsRole(
        requiredFile,
        loanOnboardingDetails as unknown as BorrowerOnboardingDetails[],
      );

    const groupedFiles = RequiredFilesByDocumentTypeGroupMap.groupRequiredFilesByDocumentGroup(
      files,
      resolveOnboardingDetailsRole,
    );

    const getOnboardingDetailsByRole = (role: OnboardingDetailsRole) =>
      paymentMethodDetailEntity.onboardingDetails(role);

    return groupedFiles.map(({ groupKey, files: groupFiles }) => ({
      groupKey,
      titleSuffix: RequiredFilesByDocumentTypeGroupMap.getParticipantGroupTitleSuffix(
        groupKey,
        getOnboardingDetailsByRole,
      ),
      files: sortGroupFilesByMode(groupFiles, sortMode),
    }));
  }, [files, paymentMethodDetailEntity, sortMode]);
};
